import { CARD_PICTURE_NAMES } from '@src/components/faces/ParticipantFace';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isPlaceholderOrganizerUsername, type BoxInfo } from '@lub0v/santa-utils';
import { Input, PicturePicker, ParticipantFace, Stack, Typography, showDialog, showToast } from '@src/components';
import { santaClient } from '@src/state/client';
import { boxMessengerPath } from '@src/routing/routes';
import './OrganizerIdentity.css';

function OrganizerNameField({
  initial,
  initialPicture,
  nameRef,
  pictureRef,
  error,
}: {
  initial: string;
  initialPicture: string;
  nameRef: { current: string };
  pictureRef: { current: string | undefined };
  error: string | null;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial);
  const [picture, setPicture] = useState(initialPicture);
  return (
    <Stack gap={5}>
    <Input
      value={name}
      onValueChange={(value) => {
        nameRef.current = value;
        setName(value);
      }}
      label={t('game.header.nameLabel')}
      hint={t('game.header.nameBody')}
      error={error ?? undefined}
      testId="game-org-name-input"
    />
    <Stack gap={2}>
      <Typography role="label/field">{t('organizersPanel.pictureLabel')}</Typography>
      <PicturePicker
        value={picture}
        pictures={CARD_PICTURE_NAMES}
        renderPicture={(name) => <ParticipantFace picture={name} />}
        onValueChange={(value) => {
          pictureRef.current = value;
          setPicture(value);
        }}
        testId="game-org-picture-picker"
      />
    </Stack>
    </Stack>
  );
}

export function useOrganizerRename(info: BoxInfo, onChanged: () => Promise<void> | void) {
  const { t } = useTranslation();
  const box = info.box;
  const adminName = (info.admin?.username || '').trim();
  const needsName = !adminName || isPlaceholderOrganizerUsername(adminName);
  function openRename() {
    if (box.isArchived) return;
    const nameRef = { current: needsName ? '' : adminName };
    const pictureRef = { current: info.admin.picture ?? undefined };
    const show = (error: string | null) => {
      showDialog({
        title: t('game.header.nameTitle'),
        children: <OrganizerNameField initial={nameRef.current} initialPicture={pictureRef.current ?? ''} nameRef={nameRef} pictureRef={pictureRef} error={error} />,
        cancel: t('box.cancel'),
        submit: t('game.header.nameSubmit'),
        onSubmit: async () => {
          const username = nameRef.current.trim();
          if (!username || isPlaceholderOrganizerUsername(username) || username.length < 2) {
            show(t('game.header.nameTooShort'));
            throw new Error('name too short');
          }
          const res = await santaClient.updateAdminUsername({
            key: box.key,
            username,
            picture: pictureRef.current,
          });
          if (!res.ok) {
            show(t(res.error?.message || 'newBox.saveFailed'));
            throw new Error('rename failed');
          }
          showToast(t('game.header.nameSaved'), 'success');
          await onChanged();
        },
        testId: 'game-org-name-dialog',
      });
    };
    show(null);
  }

  return openRename;
}

export function OrganizerIdentity({
  info,
}: {
  info: BoxInfo;
  onChanged: () => Promise<void> | void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const box = info.box;
  const organizers = info.organizers?.length ? info.organizers : [{
    id: 0, username: info.admin?.username || '—', isYou: info.isAdmin,
  }];

  function openDetails(person: { id: number; username: string; isYou: boolean }) {
    const canContact = (info.isParticipant || info.isAdmin) && !person.isYou && !box.isArchived;
    showDialog({
      title: person.username || t('game.header.organizerTitle'),
      children: t('game.header.organizerExplanation'),
      cancel: t('box.close'),
      submit: canContact ? t('game.header.contactOrganizer') : undefined,
      onSubmit: canContact ? () => navigate(boxMessengerPath(box.key, person.id ? `org-${person.id}` : 'org')) : undefined,
      testId: 'game-organizer-dialog',
    });
  }
  return (
    <>
      {t(organizers.length > 1 ? 'game.header.organizers' : 'game.header.organizer')}{' '}
      {organizers.map((person, index) => (
        <span key={person.id}>
          {index > 0 ? ', ' : null}
          {box.isArchived || person.isYou ? person.username : (
            <button type="button" className="organizer-identity__name"
              data-testid={person.isYou || organizers.length === 1 ? 'game-org-name' : `game-org-name-${person.id}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                openDetails(person);
              }}>
              {person.username}
            </button>
          )}
          {person.isYou && organizers.length === 1 ? ` ${t('game.header.thatsYou')}` : null}
        </span>
      ))}
    </>
  );
}
